"use client"

import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { Mobile, NewText, ShareIcon } from "ui"
import { extractProperty, extractPropertyMediaItems, getUmbracoMediaUrl } from "utils"
import { shareProductClick } from "utils/gtm"
import AccordionBlock from "./AccordionBlock"
import AdditionalDetailsBlock from "./AdditionalDetailsBlock"
import KeyFeaturesBlock from "./KeyFeaturesBlock"
import { StyledProductImageWrapper } from "./ProductMainContent"

const StyledInfoWrapper = styled.div`
  width: 100%;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0px;
  }
`

const StyledTopBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: flex-start;
  }
`

const StyledShareButton = styled(Button)`
  border-radius: 50%;
  min-width: 24px;
  min-height: 24px;
  width: 24px;
  height: 24px;
  border: 1px solid var(--Black, #000);
  background: var(--White, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: 40px;
    min-width: 40px;
    width: 40px;
    height: 40px;
    svg {
      width: 24px;
      height: 24px;
    }
  }
`

const StyledSeparator = styled.span`
  display: inline-block;
  width: 1px;
  height: 16px;
  background-color: #6f6f6f;
  margin: 0 16px;
`

const StyledDescription = styled(NewText)`
  p {
    margin: 0;
  }
`

function ProductDetails({
  pageData,
  productCategories,
}: {
  locale: "he" | "en"
  pageData: any
  productCategories?: any
}) {
  const productName = extractProperty(pageData, "productName")
  const productImage = extractPropertyMediaItems(pageData, "image")
  const categoriesData = productCategories
    ?.filter((el: any) => el?.node?.contentType?.alias === "productCategory")
    ?.map((el: any) => ({
      id: el.node.id,
      name: el.node.name,
      value: el.node.properties?.[0]?.propertyValue?.value,
    }))

  const categoryNames = extractProperty(pageData, "productCategory")
    ?.map((cat: any) => categoriesData?.find((c: any) => c.id === cat.id)?.value)
    ?.filter((category: string) => !!category)
    ?.join(" , ")

  const subCategoriesData = productCategories
    ?.filter((el: any) => el?.node?.contentType?.alias !== "productCategory")
    ?.map((el: any) => ({
      id: el.node.id,
      name: el.node.name,
      value: el.node.properties?.[0]?.propertyValue?.value,
    }))

  const subCategoryNames = extractProperty(pageData, "productSubCategory")
    ?.map((cat: any) => subCategoriesData?.find((c: any) => c.id === cat.id)?.value)
    ?.filter((category: string) => !!category)
    ?.join(" , ")

  const categories =
    [categoryNames, subCategoryNames]
      ?.filter((category) => !!category)
      ?.join("/span/ ")
      ?.split("/") || []

  const productDescription = extractProperty(pageData, "productDescription")
  const pathname = usePathname()
  const elementName = pathname?.includes("products-simply")
    ? "SIMPLY - products"
    : "BG - products"

  const onShare = async () => {
    shareProductClick(productName || "", categoryNames || "", elementName)
    let plainTextDescription = ""
    if (typeof window !== "undefined" && productDescription) {
      const tempDiv = document.createElement("div")
      tempDiv.innerHTML = productDescription
      plainTextDescription = tempDiv.textContent || tempDiv.innerText || ""
    }

    const shareData = {
      title: productName,
      text: plainTextDescription || "",
      url: window.location.href,
    }
    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData)
      } catch (err) {
        console.error(err)
      }
    } else {
      navigator.clipboard.writeText(shareData.url)
    }
  }

  return (
    <StyledInfoWrapper>
      <StyledTopBox>
        <NewText
          component={"h1"}
          desktopFontSize={40}
          desktopFontWeight={400}
          desktopLineHeight={1.1}
          mobileFontSize={32}
          mobileLineHeight={1}
          color="black"
          marginBottom={"8px"}
        >
          {productName}
        </NewText>
        <StyledShareButton onClick={onShare}>
          <ShareIcon />
        </StyledShareButton>
      </StyledTopBox>
      {categories?.length > 0 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          width="100%"
          marginBottom={"16px"}
        >
          {categories.map((category, index) => {
            if (category === "span") {
              return <StyledSeparator key={index} />
            }
            return (
              <NewText
                key={index}
                component="p"
                desktopFontSize={16}
                desktopFontWeight={400}
                desktopLineHeight={"20px"}
                mobileFontSize={16}
                mobileFontWeight={400}
                mobileLineHeight={"100%"}
                color="black"
                textTransform={"capitalize"}
              >
                {category}
              </NewText>
            )
          })}
        </Box>
      )}

      <StyledDescription
        component="h2"
        desktopFontSize={24}
        desktopFontWeight={400}
        desktopLineHeight={"100%"}
        mobileFontSize={20}
        mobileFontWeight={400}
        mobileLineHeight={"100%"}
        color="black"
        dangerouslySetInnerHTML={{
          __html: productDescription,
        }}
      />
      {productImage?.[0] && (
        <Mobile sx={{ width: "fit-content", alignSelf: "center" }}>
          <StyledProductImageWrapper>
            <Image
              src={getUmbracoMediaUrl(productImage?.[0])}
              alt={`${productName} Image`}
              fill
              style={{ objectFit: "contain" }}
              priority
            />
          </StyledProductImageWrapper>
        </Mobile>
      )}

      <KeyFeaturesBlock data={pageData} />

      <AdditionalDetailsBlock data={pageData} />

      <AccordionBlock data={pageData} />
    </StyledInfoWrapper>
  )
}
export default ProductDetails
